import React, { useCallback, useEffect, useState } from 'react';
import {
  collectTeamsDebugSnapshot,
  enrichTeamsDebugSnapshot,
  formatTeamsDebugSnapshot,
  type TeamsDebugSnapshot,
} from '../utils/teamsDebugInfo';
type TeamsDebugPanelProps = {
  theme: 'dark' | 'light';
};

function boolLabel(value: boolean): string {
  return value ? 'yes' : 'no';
}

const TeamsDebugPanel: React.FC<TeamsDebugPanelProps> = ({ theme }) => {
  const [snapshot, setSnapshot] = useState<TeamsDebugSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const isDark = theme === 'dark';
  const labelClass = isDark ? 'text-gray-400' : 'text-gray-500';
  const valueClass = isDark ? 'text-gray-200' : 'text-gray-800';
  const boxClass = isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-300';

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const base = collectTeamsDebugSnapshot();
      const full = await enrichTeamsDebugSnapshot(base);
      setSnapshot(full);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const copyDebug = () => {
    if (!snapshot) return;
    const text = formatTeamsDebugSnapshot(snapshot);
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(text);
    } else {
      window.prompt('Copy debug info:', text);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const rows: Array<{ label: string; value: string; highlight?: boolean }> = snapshot
    ? [
        { label: 'Full page URL (iframe)', value: snapshot.pageUrl, highlight: true },
        { label: 'Query string', value: snapshot.search, highlight: true },
        { label: 'teams= query param', value: snapshot.teamsQueryParam ?? '(missing)', highlight: true },
        { label: 'teams=true present', value: boolLabel(snapshot.hasTeamsQueryParam), highlight: true },
        { label: 'Pathname', value: snapshot.pathname },
        { label: 'In Teams iframe', value: boolLabel(snapshot.inIframe) },
        { label: 'embeddedOnLoad', value: boolLabel(snapshot.embeddedOnLoad) },
        { label: 'Teams session flag', value: boolLabel(snapshot.teamsSessionFlag) },
        { label: 'Teams auth in progress', value: boolLabel(snapshot.teamsAuthInProgress) },
        { label: 'SDK initialized', value: boolLabel(snapshot.sdkInitialized) },
        { label: 'SDK ready', value: boolLabel(snapshot.sdkReady) },
        { label: 'Teams link mode active', value: boolLabel(snapshot.shouldOpenInTeamsHost) },
        { label: 'Host client type', value: snapshot.hostClientType ?? '(unknown)' },
        { label: 'Context host name', value: snapshot.contextHostName ?? '(n/a)' },
        { label: 'Frame context', value: snapshot.contextFrameContext ?? '(n/a)' },
        { label: 'Context error', value: snapshot.contextError ?? '(none)' },
        { label: 'Last link click', value: snapshot.lastLinkAttempt ?? '(none)', highlight: true },
        { label: 'Last raw pointer hit', value: snapshot.lastRawClick ?? '(none)', highlight: true },
        { label: 'document.referrer', value: snapshot.documentReferrer },
      ]
    : [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className={`text-xs ${labelClass}`}>
          Diagnostics for the URL loaded inside the Teams tab iframe.
        </p>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={loading}
            className="rounded-md border border-cyan-500/40 px-2 py-1 text-xs text-cyan-400 hover:bg-cyan-500/10 disabled:opacity-50"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            onClick={copyDebug}
            disabled={!snapshot}
            className="rounded-md border border-gray-600 px-2 py-1 text-xs text-gray-300 hover:border-gray-500 disabled:opacity-50"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>

      <div className={`rounded-lg border p-3 space-y-2 text-xs font-mono ${boxClass}`}>
        {rows.map((row) => (
          <div key={row.label} className="grid grid-cols-1 gap-0.5 sm:grid-cols-[11rem_1fr] sm:gap-3">
            <div className={labelClass}>{row.label}</div>
            <div
              className={`${valueClass} break-all ${row.highlight ? 'text-cyan-300' : ''}`}
              title={row.value}
            >
              {row.value}
            </div>
          </div>
        ))}
        {!snapshot && !loading ? (
          <div className={labelClass}>No debug data yet.</div>
        ) : null}
      </div>

    </div>
  );
};

export default TeamsDebugPanel;
